import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BarChart3, Globe, X } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";
import { Panel, T, btnPrimary, btnSecondary, inputCls } from "@/components/admin/ui";

const TYPE_LABELS = {
  home: "Accueil", chauffeur: "Hub chauffeur", city: "Ville", airport: "Aéroport",
  "airport-index": "Hub aéroports", routes: "Index routes", route: "Route",
  fleet: "Flotte", faq: "FAQ", contact: "Contact", business: "Business", chauffeurs: "Chauffeurs",
};
const LANGS = ["fr", "en", "de"];

export default function AdminSeo() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [editing, setEditing] = useState(null); // { page_type, page_id, lang, title, meta, noindex }
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    adminApi.get("/seo/pages").then((r) => setData(r.data)).catch(() => {});
    adminApi.get("/seo/analytics").then((r) => setStats(r.data)).catch(() => {});
  }, [reload]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/seo/pages/override", {
        page_type: editing.page_type, page_id: editing.page_id, lang: editing.lang,
        title: editing.title || null, meta: editing.meta || null, noindex: editing.noindex,
      });
      toast.success("Page mise à jour — prise en compte au prochain déploiement");
      setEditing(null);
      setReload((r) => r + 1);
    } catch (err) {
      toast.error(apiError(err, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  const pages = (data?.pages || []).filter((pg) => !typeFilter || pg.type === typeFilter);
  const alertCount = (data?.pages || []).reduce(
    (n, pg) => n + LANGS.reduce((m, l) => m + (pg.langs[l]?.alerts?.length || 0), 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-seo">
      <header>
        <h1 className="text-2xl font-bold text-ink">SEO</h1>
        <p className="mt-1 text-sm text-slate">
          {data ? `${data.total} pages localisées · ${alertCount} alerte(s)` : "Chargement…"} — les modifications de contenu sont appliquées au prochain déploiement.
        </p>
      </header>

      {stats && (
        <Panel title="Acquisition — 30 derniers jours" testId="seo-analytics">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["Pages vues", stats.by_event?.page_view || 0],
              ["Réservations démarrées", stats.by_event?.booking_started || 0],
              ["Réservations payées", stats.by_event?.payment_completed || 0],
              ["Leads Business", (stats.by_event?.business_lead || 0) + (stats.by_event?.business_cta || 0)],
            ].map(([label, v]) => (
              <div key={label} className="rounded-xl border border-ink/[0.06] bg-cloud/50 px-4 py-3">
                <p className="text-xs text-slate">{label}</p>
                <p className="mt-1 font-mono text-xl font-bold text-ink">{v}</p>
              </div>
            ))}
          </div>
          {stats.top_pages?.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate"><BarChart3 className="h-3.5 w-3.5" /> Pages les plus vues</p>
              <div className="space-y-1">
                {stats.top_pages.slice(0, 8).map((p) => (
                  <div key={p.path} className="flex items-center justify-between rounded-lg px-2 py-1 text-xs odd:bg-cloud/40">
                    <span className="truncate font-mono text-slate">{p.path}</span>
                    <span className="font-mono font-semibold text-ink">{p.views}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      )}

      <Panel title="Pages" testId="seo-pages">
        <div className="mb-4 flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setTypeFilter("")}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${!typeFilter ? "bg-ink text-white" : "border border-ink/10 text-slate hover:border-miles/40"}`}>
            Toutes
          </button>
          {Object.entries(TYPE_LABELS).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTypeFilter(k)} data-testid={`seo-filter-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${typeFilter === k ? "bg-ink text-white" : "border border-ink/10 text-slate hover:border-miles/40"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {pages.map((pg) => {
            const alerts = LANGS.flatMap((l) => (pg.langs[l]?.alerts || []).map((a) => `${l}: ${a}`));
            return (
              <div key={`${pg.type}-${pg.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-ink/[0.06] px-4 py-3"
                data-testid={`seo-page-${pg.type}-${pg.id || "index"}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">
                    {TYPE_LABELS[pg.type] || pg.type}{pg.id ? ` — ${pg.id}` : ""}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {LANGS.map((l) => {
                      const info = pg.langs[l];
                      return (
                        <button key={l} type="button"
                          onClick={() => setEditing({ page_type: pg.type, page_id: pg.id, lang: l, title: info?.title || "", meta: info?.meta || "", noindex: info?.noindex || false })}
                          data-testid={`seo-edit-${pg.type}-${pg.id || "index"}-${l}`}
                          className={`rounded-md px-2 py-1 font-mono text-[10px] font-semibold uppercase transition-colors ${
                            info?.noindex ? "bg-red-50 text-red-600" : info?.overridden ? "bg-miles-ice text-miles" : "bg-cloud text-slate hover:bg-miles-ice hover:text-miles"}`}>
                          {l}{info?.overridden ? " ✎" : ""}
                        </button>
                      );
                    })}
                    {alerts.length > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700">
                        <AlertTriangle className="h-3 w-3" /> {alerts.join(" · ")}
                      </span>
                    )}
                  </div>
                </div>
                <Globe className="h-4 w-4 shrink-0 text-slate/40" />
              </div>
            );
          })}
        </div>
      </Panel>

      {editing && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/40 p-4" onClick={() => setEditing(null)} data-testid="seo-edit-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink">
                {TYPE_LABELS[editing.page_type]}{editing.page_id ? ` — ${editing.page_id}` : ""} <span className="font-mono text-sm uppercase text-miles">({editing.lang})</span>
              </h2>
              <button type="button" onClick={() => setEditing(null)} className="rounded-lg p-1.5 text-slate hover:bg-cloud"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate">Title SEO <span className="font-normal">(vide = contenu par défaut)</span></label>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid="seo-edit-title" className={`${inputCls} mt-1`} maxLength={200} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate">Meta description</label>
                <textarea value={editing.meta} onChange={(e) => setEditing({ ...editing, meta: e.target.value })}
                  data-testid="seo-edit-meta" rows={3} className={`${inputCls} mt-1 resize-none`} maxLength={400} />
              </div>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={editing.noindex} onChange={(e) => setEditing({ ...editing, noindex: e.target.checked })}
                  data-testid="seo-edit-noindex" className="h-4 w-4 rounded accent-miles" />
                noindex (retirer la page de Google)
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className={btnSecondary}>Annuler</button>
              <button type="button" onClick={save} disabled={saving} data-testid="seo-edit-save" className={`${btnPrimary} disabled:opacity-50`}>
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
